import React, { createContext, useContext } from 'react'
import { navigate } from 'gatsby'
import { map } from 'lodash/fp'

import api, { parseJSON } from '../utils/api'

import { getUser, setUser, isLoggedIn } from '../utils/localStorage'
import { withNotifier } from './Notifications'
import { isEqual } from 'lodash/fp'

import HtmlValidationHelper from './HtmlValidationHelper'

const decorate = withNotifier

let AppContext
const { Consumer, Provider } = (AppContext = createContext({}))

const initialParams = {
  sort: {},
  showGenerateReportDiaglog: false,
  ready: false,
  patients: [],
  ids: [],
  meta: {
    limit: 20,
    page: 0,
  },
  filters: { search: '' },
  selection: [],
}
class App extends React.Component {
  constructor() {
    super()

    const user = isLoggedIn()
      ? getUser()
      : {
          token: null,
          expiresAt: null,
          userId: null,
          userPermissions: [],
          userRoles: [],
          unitId: null,
        }
    const params = initialParams
    this.state = {
      ...user,
      params: { ...params },
      filtersActive: false,
      hiddenSchoolSection: false,
      guardianAddress: false,
      login: this.login,
      logout: this.logout,
      refreshToken: this.refreshToken,
      withAuth: this.withAuth,
      toggleFilters: this.toggleFilters,
      resetPassword: this.resetPassword,
      setPassword: this.setPassword,
      updateParams: this.updateParams,
      resetParams: this.resetParams,
      handleTabsChange: this.handleTabsChange,
      onClickHideSchool: this.onClickHideSchool,
      onOpenGuardianAddressModal: this.onOpenGuardianAddressModal,
    }
  }

  componentDidMount = () => {
    HtmlValidationHelper.fixPipeInGoogleapisLinks();
    HtmlValidationHelper.fixMetaCharsetPlace();
    HtmlValidationHelper.deleteNotAllowedAttributes();
  }

  onClickHideSchool = () => {
    this.setState({ hiddenSchoolSection: !this.state.hiddenSchoolSection })
  }

  resetSchoolSection = () => {
    this.setState({ hiddenSchoolSection: false })
  }

  onOpenGuardianAddressModal = () => {
    this.setState({ guardianAddress: !this.state.guardianAddress })
  }
  resetGuardianAddressModal = () => {
    this.setState({ guardianAddress: false })
  }

  toggleFilters = () => {
    this.setState({ filtersActive: !this.state.filtersActive })
  }

  updateParams = async (newParams, callback) => {
    this.setState({ params: { ...this.state.params, ...newParams } })
  }

  resetParams = () => {
    this.setState({ params: initialParams, filtersActive: false })
  }

  login = async (payload) => {
    try {
      const {
        expiresAt,
        token,
        user: { roles, id, unitId },
      } = await api('/user/rest/user/login', {
        method: 'POST',
        payload,
      })

      const userPermissions = roles.reduce(
        (acc, role) => [...acc, ...role.permissions],
        []
      )
      const userRoles = map('name', roles)
      const userLevel = Math.max.apply(null, map('level', roles))
      const user = {
        token,
        expiresAt,
        userId: id,
        userPermissions,
        userRoles,
        userLevel,
        unitId: unitId,
      }

      this.setState(
        () => user,
        () => {
          setUser(user)
          navigate(`app`)
        }
      )
    } catch (e) {
      const errorResponse = await this.handleErrors(e)
      if (errorResponse) {
        this.props.notify('Niepoprawne logowanie', 'error')
        const errors = {}
        errors.password = 'generic'
        return errors
      }
    }
  }
  resetPassword = async (payload) => {
    try {
      await api('/user/rest/reset-password', {
        method: 'POST',
        headers: { responseType: 'json' },
        payload,
      })
    } catch (e) {
      this.handleErrors(e)
    }
  }

  setPassword = async (payload) => {
    try {
      await api('/user/rest/reset-password/reset', {
        method: 'POST',
        headers: { responseType: 'json' },
        payload,
      })
      navigate('/app/users')
      this.props.notify('Hasło zostało zmienione', 'success')
    } catch (e) {
      this.handleErrors(e)
    }
  }

  refreshToken = async () => {
    try {
      const {
        expiresAt,
        token,
        user: { roles, id, unitId },
      } = await api('/user/rest/user/refresh-token', {
        method: 'POST',
        headers: {
          responseType: 'json',
          Authorization: `Bearer ${this.state.token}`,
        },
      })

      const userPermissions = roles.reduce(
        (acc, role) => [...acc, ...role.permissions],
        []
      )
      const userRoles = map('name', roles)
      const userLevel = Math.max.apply(null, map('level', roles))
      const user = {
        token,
        expiresAt,
        userId: id,
        userPermissions,
        userRoles,
        userLevel,
        unitId: unitId,
      }

      this.setState(() => user)
      setUser(user)
    } catch (e) {
      this.handleErrors(e)
    }
  }

  logout = () => {
    setUser({})
    navigate('/app/login')
  }

  withAuth = async (endpoint, options, throwOnError = false) => {
    if (!this.state.token) {
      navigate(`app`)
    } else {
      try {
        const response = await api(endpoint, {
          ...options,
          headers: {
            ...options.headers,
            Authorization: `Bearer ${this.state.token}`,
          },
        })
        return response
      } catch (e) {
        const err = this.handleErrors(e, throwOnError)
        console.error(err)
        return err
      }
    }
  }

  handleErrors = async (e, throwOnError) => {
    if (e.response) {
      switch (e.response.status) {
        case 401:
          this.props.notify('Zostałeś wylogowany', 'error')
          return this.logout()

        case 400: {
          const response = await parseJSON(e.response)
          return response
        }
        case 404: {
          if (throwOnError) {
            throw e.response
          }
        }
        case 409: {
          throw e.response
        }
        case 428: {
          throw e.response
        }
        case 403:
          this.props.notify(
            'Brak wymaganegoo uprawnienia do wykonania tej operacji',
            'error'
          )

        default:
          ![401, 403, 404, 409].includes(e.response.status)
            ? this.props.notify(
                'Wystąpił nieoczekiwany błąd. Proszę spróbować ponownie',
                'error'
              )
            : null
      }
    } else {
      this.props.notify('Wystąpił nieoczekiwany błąd', 'error')
    }
  }

  componentDidUpdate() {
    HtmlValidationHelper.deleteNotAllowedAttributes();

    const patientRegex = /app\/patients\/\d+$/i
    const patientRegexAdd = /app\/patients\/\add/i

    if (
      !patientRegex.test(window.location.pathname) &&
      !patientRegexAdd.test(window.location.pathname) &&
      this.state.hiddenSchoolSection
    ) {
      this.resetSchoolSection()
    } else if (
      !patientRegex.test(window.location.pathname) &&
      !patientRegexAdd.test(window.location.pathname) &&
      this.state.guardianAddress
    ) {
      this.resetGuardianAddressModal()
    }

    const isOnPatietRelatedPage = window.location.href.includes('patients')
    if (!isOnPatietRelatedPage && !isEqual(this.state.params, initialParams)) {
      this.resetParams()
    } else return
  }

  render() {
    return <Provider value={this.state}>{this.props.children}</Provider>
  }
}

export const withUserPermissions = (WrappedComponent) => (props) => (
  <Consumer>
    {({ userPermissions, userRoles, userId, userLevel }) => (
      <WrappedComponent
        userPermissions={userPermissions}
        userRoles={userRoles}
        userId={userId}
        userLevel={userLevel}
        {...props}
      />
    )}
  </Consumer>
)

App.Consumer = Consumer
App.Provider = decorate(App)

export const useWithAuth = () => {
  const { withAuth } = useContext(AppContext)
  return withAuth
}

export const useLogout = () => {
  const { logout } = useContext(AppContext)
  return logout
}

export const useSession = () => {
  const session = useContext(AppContext)
  return session
}

export default App
export { AppContext }
